import {
  Category,
  GraphWithProjects,
  Graph,
  Project,
  Subcategory,
  CategoryColor,
  ContentFormat,
} from '../api/api.ts';
import { ResetViewCallback, useMapContext } from './Map.tsx';
import classNames from 'classnames';
import {
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faMagnifyingGlassPlus,
} from '@fortawesome/free-solid-svg-icons';
import {
  backgroundColorClassesForColor,
  borderColorClassesForColor,
  gradientClassesForColor,
  hoverTextColorClassesForColor,
} from '../utils/Colors.ts';
import NeumorphBox, { NeumorphType } from '../common/ui/NeumorphBox.tsx';
import GraphLink from '../common/GraphLink.tsx';
import { ArrowRight, ArrowUp } from 'lucide-react';
import NewGraphText from './NewGraphText.tsx';
import NewGraphSwitch, { ExplorationView } from './NewGraphSwitch.tsx';
import { LanguageContext } from '@/common/Language.tsx';

enum SelectionType {
  Project = 'project',
  Subcategory = 'sub-category',
  Category = 'category',
}
type Selection =
  | {
      type: SelectionType.Project;
      project: Project;
      subcategory: GraphWithProjects['categories'][number]['subcategories'][number];
      category: GraphWithProjects['categories'][number];
    }
  | {
      type: SelectionType.Subcategory;
      subcategory: GraphWithProjects['categories'][number]['subcategories'][number];
      category: GraphWithProjects['categories'][number];
    }
  | {
      type: SelectionType.Category;
      category: GraphWithProjects['categories'][number];
    };

export default function NewGraph({
  graph,
  projects,
  resetViewCallback,
  cancel,
}: {
  graph: Graph;
  projects: Project[];
  resetViewCallback: ResetViewCallback;
  cancel: () => void;
}) {
  const [explorationView, setExplorationView] = useState<ExplorationView>(
    ExplorationView.Graph
  );
  const { t } = useContext(LanguageContext);

  const sidebarRef = useRef(null);
  const projectsForSubcategoryId = useCallback(
    (subcategoryId: string) =>
      projects.filter((p) => p.subcategoryId === subcategoryId),
    [projects]
  );
  const enrichedGraph = useMemo<GraphWithProjects>(() => {
    return {
      ...graph,
      categories: graph.categories.map((c) => ({
        ...c,
        subcategories: c.subcategories.map((sc) => ({
          ...sc,
          projects: [...projectsForSubcategoryId(sc.id)],
        })),
      })),
    };
  }, [graph, projectsForSubcategoryId]);
  const [relatedThreshold] = useState(0.5);

  const [selectedIndexes, setSelectedIndexes] = useState<{
    category: number;
    subcategory: number;
    project: number;
  }>({ category: -1, subcategory: -1, project: -1 });

  useEffect(() => {
    const sidebar = sidebarRef.current as HTMLElement | null;
    if (sidebar) sidebar.scrollTop = 0;
  }, [selectedIndexes]);

  const setIndexesForProjectId = (projectId: string) => {
    for (
      let categoryIndex = 0;
      categoryIndex < enrichedGraph.categories.length;
      categoryIndex++
    ) {
      const category = enrichedGraph.categories[categoryIndex];
      for (
        let subcategoryIndex = 0;
        subcategoryIndex < category.subcategories.length;
        subcategoryIndex++
      ) {
        const subcategory = category.subcategories[subcategoryIndex];
        for (
          let projectIndex = 0;
          projectIndex < subcategory.projects.length;
          projectIndex++
        ) {
          if (subcategory.projects[projectIndex].id === projectId) {
            setSelectedIndexes({
              category: categoryIndex,
              subcategory: subcategoryIndex,
              project: projectIndex,
            });
            return;
          }
        }
      }
    }
    throw `could not find indexes for projectId ${projectId}`;
  };

  const selection = useMemo<Selection | null>(() => {
    if (selectedIndexes.category === -1) {
      return null;
    } else if (selectedIndexes.subcategory === -1) {
      const category = enrichedGraph.categories[selectedIndexes.category];
      if (!category)
        throw `No category found for selection index ${selectedIndexes.category}`;
      return {
        type: SelectionType.Category,
        category: category,
      };
    } else if (selectedIndexes.project === -1) {
      const category = enrichedGraph.categories[selectedIndexes.category];
      if (!category)
        throw `No category found for selection index ${selectedIndexes.category}`;
      const subcategory = category.subcategories[selectedIndexes.subcategory];
      if (!subcategory)
        throw `No subcategory found for selection index ${selectedIndexes.subcategory}`;
      return {
        type: SelectionType.Subcategory,
        category: category,
        subcategory: subcategory,
      };
    } else {
      const category = enrichedGraph.categories[selectedIndexes.category];
      if (!category)
        throw `No category found for selection index ${selectedIndexes.category}`;
      const subcategory = category.subcategories[selectedIndexes.subcategory];
      if (!subcategory)
        throw `No subcategory found for selection index ${selectedIndexes.subcategory}`;
      const project =
        enrichedGraph.categories[selectedIndexes.category].subcategories[
          selectedIndexes.subcategory
        ].projects[selectedIndexes.project];
      if (!project)
        throw `No project found for selection index ${selectedIndexes.project}`;

      return {
        type: SelectionType.Project,
        category: category,
        subcategory: subcategory,
        project: project,
      };
    }
  }, [selectedIndexes, enrichedGraph, projects]);

  const getRelatedIds = useCallback(
    (project: Project) => {
      if (!projects) return [];
      return Object.entries(project.relations)
        .filter(([_key, value]) => value > relatedThreshold)
        .map((keyValueTuple) => keyValueTuple[0]);
    },
    [projects, relatedThreshold]
  );

  const projectById = useCallback(
    (id: string): Project | undefined => {
      const result = projects.find((project: Project) => project.id === id);
      // if (!result) throw `No project found for id '${id}'`;
      return result;
    },
    [projects]
  );

  const subcategoryById = useCallback(
    (id: string): Subcategory & { projects: Project[] } => {
      const result = enrichedGraph.categories
        .flatMap((cat) => cat.subcategories)
        .find((sub) => sub.id === id);
      if (!result) throw `No subcategory found for id '${id}'`;
      return {
        ...result,
        projects: projects.filter((p) => p.subcategoryId === result.id),
      };
    },
    [enrichedGraph, projects]
  );

  const categoryById = useCallback(
    (
      id: string
    ): Category & {
      subcategories: (Subcategory & { projects: Project[] })[];
    } => {
      const result = enrichedGraph.categories.find((cat) => cat.id === id);
      if (!result) throw `No category found for id '${id}'`;
      return {
        ...result,
        subcategories: result.subcategories.map((sub) => ({
          ...sub,
          projects: projects.filter((p) => p.subcategoryId === sub.id),
        })),
      };
    },
    [enrichedGraph, projects]
  );

  const relatedToSelected = useMemo((): Project[] => {
    if (selection?.type !== SelectionType.Project) return [];
    const relatedIds = getRelatedIds(selection.project);
    return relatedIds.map((id) => projectById(id)).filter((p) => !!p);
  }, [selection, projectById, getRelatedIds]);

  const loadProject = useCallback(
    (project: Project) => {
      const relatedIds = getRelatedIds(project);
      resetViewCallback(project.id, relatedIds);
      cancel();
    },
    [getRelatedIds, resetViewCallback, cancel]
  );

  const coordinateForCircle = useCallback(
    (
      radius: number,
      degreeBetweenItems: number,
      scaleX: number,
      centerOnSelected: 'full' | 'half' | 'no',
      index: number,
      selectedIndex: number,
      total: number
    ) => {
      const degreeTotal = degreeBetweenItems * (total - 1);
      let startAtFromMiddle;
      if (selectedIndex !== -1) {
        switch (centerOnSelected) {
          case 'full':
            startAtFromMiddle = selectedIndex * degreeBetweenItems;
            break;
          case 'half':
            startAtFromMiddle =
              (Math.round(degreeTotal / 2) +
                selectedIndex * degreeBetweenItems) /
              2;
            break;
          default:
            startAtFromMiddle = Math.round(degreeTotal / 2);
            break;
        }
      } else {
        startAtFromMiddle = Math.round(degreeTotal / 2);
      }
      const startAt = 90 - startAtFromMiddle;
      const pos = (startAt + index * degreeBetweenItems) / 180;
      return {
        x: radius * scaleX * Math.cos((pos + 1) * Math.PI),
        y: radius * Math.sin(pos * Math.PI),
      };
    },
    [selectedIndexes]
  );

  const lineOffsetOrigin = 20;
  const lineOffsetTarget = 10;
  const shortenLength = 40;
  const lengthForLine = (
    { x: fromX, y: fromY }: { x: number; y: number },
    { x: toX, y: toY }: { x: number; y: number }
  ) => {
    // adjusted y coordinates to not make the line hit
    const adjustedToY = toY - lineOffsetTarget * Math.sign(toY - fromY);
    const adjustedFromY = fromY + lineOffsetOrigin * Math.sign(toY - fromY);
    const dX = Math.round(toX - fromX);
    const dY = Math.round(adjustedToY - adjustedFromY);
    return (
      Math.sqrt(Math.pow(dX, 2) + Math.pow(dY, 2)) -
      (shortenLength / 2) *
        (Math.abs(
          angleForLine({ x: fromX, y: fromY }, { x: toX, y: toY }) - 90
        ) /
          90)
    );
  };

  const angleForLine = (
    { x: fromX, y: fromY }: { x: number; y: number },
    { x: toX, y: toY }: { x: number; y: number },
    log = false
  ) => {
    if (log) {
      console.log('');
      console.log(fromX, fromY, toX, toY);
    }
    // adjusted y coordinates to not make the line hit
    const adjustedToY = toY - lineOffsetTarget * Math.sign(toY - fromY);
    const adjustedFromY = fromY + lineOffsetOrigin * Math.sign(toY - fromY);
    const dX = toX - fromX;
    const dY = adjustedToY - adjustedFromY;

    const result =
      (dX >= 0 ? -1 : 1) * // right half, left half
      (-(Math.PI / 2) + (dX >= 0 ? 1 : -1) * Math.atan(dY / dX));
    if (log) {
      console.log(
        'X:',
        Math.round(fromX),
        ' => ',
        Math.round(toX),
        ' | dX: ',
        Math.round(dX)
      );
      console.log(
        'Y:',
        Math.round(fromY),
        ' => ',
        Math.round(toY),
        ' | dY: ',
        Math.round(dY)
      );
      console.log(result);
    }
    return result;
  };

  const fanConfig: Record<
    'category' | 'subcategory' | 'project',
    {
      radius: number;
      degreePerItem: number;
      scaleX: number;
      yOffset: number;
    }
  > = {
    category: {
      radius: 40,
      degreePerItem: 0, // currently overridden with 360 / length
      scaleX: 4,
      yOffset: -40,
    },
    subcategory: {
      radius: 100,
      degreePerItem: 40,
      scaleX: 3,
      yOffset: 0,
    },
    project: {
      radius: 100,
      degreePerItem: 40,
      scaleX: 4,
      yOffset: 100,
    },
  };

  const coordinateForCategory = useCallback(
    (index: number) => {
      const coordinate = coordinateForCircle(
        fanConfig.category.radius,
        360 / enrichedGraph.categories.length,
        fanConfig.category.scaleX,
        'full',
        index,
        selectedIndexes.category,
        enrichedGraph.categories.length
      );
      return {
        x: coordinate.x,
        y: fanConfig.category.yOffset + coordinate.y,
      };
    },
    [selectedIndexes, enrichedGraph]
  );
  const coordinateForSubcategory = useCallback(
    (index: number) => {
      const coordinate = coordinateForCircle(
        fanConfig.subcategory.radius,
        fanConfig.subcategory.degreePerItem,
        fanConfig.subcategory.scaleX,
        'half',
        index,
        selectedIndexes.subcategory,
        enrichedGraph.categories[selectedIndexes.category].subcategories.length
      );
      return {
        x: coordinate.x,
        y: fanConfig.subcategory.yOffset + coordinate.y,
      };
    },
    [selectedIndexes, enrichedGraph]
  );
  const coordinateForProject = useCallback(
    (index: number) => {
      const coordinate = coordinateForCircle(
        fanConfig.project.radius,
        fanConfig.project.degreePerItem,
        fanConfig.project.scaleX,
        'half',
        index,
        selectedIndexes.project,
        enrichedGraph.categories[selectedIndexes.category].subcategories[
          selectedIndexes.subcategory
        ].projects.length
      );
      return {
        x: coordinate.x,
        y: fanConfig.project.yOffset + coordinate.y,
      };
    },
    [selectedIndexes, enrichedGraph]
  );

  const zIndexBackground = 'z-[0]';
  const zIndexShadow = 'z-[1]';
  const zIndexLine = 'z-[2]';
  const zIndexButton = 'z-[3]';

  const categoryClasses: Record<CategoryColor, string[]> = {
    [CategoryColor.Blue]: [
      'text-cliwac-blue-faded',
      'bg-white',
      'hover:text-white',
      'hover:bg-cliwac-blue',
    ],
    [CategoryColor.Red]: [
      'text-cliwac-red-faded',
      'bg-white',
      'hover:text-white',
      'hover:bg-cliwac-red',
    ],
    [CategoryColor.Green]: [
      'text-cliwac-green-faded',
      'bg-white',
      'hover:text-white',
      'hover:bg-cliwac-green',
    ],
  };
  const projectAndSubcategoryClasses: Record<CategoryColor, string[]> = {
    [CategoryColor.Blue]: [
      'bg-white',
      'hover:text-white',
      'hover:bg-cliwac-blue',
    ],
    [CategoryColor.Red]: [
      'bg-white',
      'hover:text-white',
      'hover:bg-cliwac-red',
    ],
    [CategoryColor.Green]: [
      'bg-white',
      'hover:text-white',
      'hover:bg-cliwac-green',
    ],
  };
  const highlightedClasses: Record<CategoryColor, string[]> = {
    [CategoryColor.Blue]: ['text-cliwac-blue', 'bg-white'],
    [CategoryColor.Red]: ['text-cliwac-red', 'bg-white'],
    [CategoryColor.Green]: ['text-cliwac-green', 'bg-white'],
  };
  const selectedClasses: Record<CategoryColor, string[]> = {
    [CategoryColor.Blue]: ['bg-cliwac-blue', 'text-white'],
    [CategoryColor.Red]: ['bg-cliwac-red', 'text-white'],
    [CategoryColor.Green]: ['bg-cliwac-green', 'text-white'],
  };

  const Button = ({
    children,
    styling,
    active,
    childActive,
    color,
    onClick,
    position,
  }: {
    children: ReactNode;
    styling: 'category' | 'other' | 'round';
    active: boolean;
    childActive: boolean;
    color: CategoryColor;
    onClick: () => void;
    position: { x: number; y: number };
  }) => {
    return (
      <div
        className={classNames('absolute', 'transition-all')}
        style={{
          left: `calc(50% + ${position.x}px)`,
          bottom: `${position.y}px`,
        }}
      >
        <div
          onClick={onClick}
          className={classNames(
            'absolute',
            styling !== 'round' ? ['py-[6px]', 'px-[24px]'] : ['p-[6px]'],
            styling === 'round' ? 'rounded-full' : 'rounded-[6px]',
            zIndexButton,
            'origin-bottom',
            'bottom-0',
            'left-0',
            'border-2',
            'border-white',
            '-translate-x-1/2',
            styling === 'round'
              ? ['w-[70px]', 'aspect-square']
              : styling === 'category'
                ? 'w-[270px]'
                : 'w-[180px]',
            'cursor-pointer',
            styling === 'round'
              ? ['flex', 'flex-col', 'items-center', 'justify-center']
              : 'text-center',
            'transition-all',
            styling === 'category' && active ? 'text-[24px]' : 'text-[18px]',
            active
              ? !childActive
                ? selectedClasses[color]
                : highlightedClasses[color]
              : styling === 'category'
                ? categoryClasses[color]
                : projectAndSubcategoryClasses[color]
          )}
        >
          {children}
        </div>
        <div
          className={classNames(
            'absolute',
            styling !== 'round' ? ['py-[6px]', 'px-[24px]'] : ['p-[6px]'],
            styling === 'round' ? 'rounded-full' : 'rounded-[6px]',
            zIndexShadow,
            'blur-sm',
            'bottom-0',
            'left-0',
            '-translate-x-1/2',
            'origin-bottom',
            styling === 'round'
              ? ['w-[80px]', 'aspect-square']
              : styling === 'category'
                ? 'w-[270px]'
                : 'w-[180px]',
            'cursor-pointer',
            styling === 'round'
              ? ['flex', 'flex-col', 'items-center', 'justify-center']
              : 'text-center',
            'transition-all',
            'text-transparent',
            styling !== 'round' ? 'bg-black' : 'bg-black/40',
            'rotate-x-70'
          )}
        >
          {children}
        </div>
      </div>
    );
  };

  const CategoryButton = ({
    category,
    categoryIndex,
    active,
    childActive,
    color,
    onClick,
  }: {
    category: Category;
    categoryIndex: number;
    active: boolean;
    childActive: boolean;
    color: CategoryColor;
    onClick: () => void;
  }) => {
    const coordinate = coordinateForCategory(categoryIndex);
    return (
      <Button
        styling={'category'}
        active={active}
        childActive={childActive}
        color={color}
        onClick={onClick}
        position={coordinate}
      >
        {t(category.label, category.labelDe)}
      </Button>
    );
  };

  const SubcategoryButton = ({
    subcategory,
    subcategoryIndex,
    active,
    childActive,
    color,
    onClick,
  }: {
    subcategory: Subcategory;
    subcategoryIndex: number;
    active: boolean;
    childActive: boolean;
    color: CategoryColor;
    onClick: () => void;
  }) => {
    const coordinate = coordinateForSubcategory(subcategoryIndex);
    return (
      <Button
        styling={'other'}
        active={active}
        childActive={childActive}
        color={color}
        onClick={onClick}
        position={coordinate}
      >
        {t(subcategory.label, subcategory.labelDe)}
      </Button>
    );
  };

  const ProjectButton = ({
    project,
    projectIndex,
    active,
    color,
    onClick,
  }: {
    project: Project;
    projectIndex: number;
    active: boolean;
    color: CategoryColor;
    onClick: () => void;
  }) => {
    const coordinate = coordinateForProject(projectIndex);

    return (
      <Button
        styling={'other'}
        active={active}
        childActive={false}
        color={color}
        onClick={onClick}
        position={coordinate}
      >
        {t(project.shortName, project.shortNameDe)}
      </Button>
    );
  };

  const Line = ({
    from,
    to,
    active,
  }: {
    from: { x: number; y: number };
    to: { x: number; y: number };
    active: boolean;
  }) => {
    return (
      <div
        className={classNames(
          'w-0',
          zIndexLine,
          'absolute',
          'origin-bottom',
          'border-l-3',
          active
            ? [
                borderColorClassesForColor(
                  enrichedGraph.categories[selectedIndexes.category].color
                ),
              ]
            : ['border-dashed', 'border-gray-400']
        )}
        style={{
          left: `calc(50% + ${from.x}px)`,
          bottom: `${from.y + lineOffsetOrigin}px`,
          height: `${lengthForLine(from, to)}px`,
          transform: `translateX(-50%) rotate(${angleForLine(from, to)}rad)`,
        }}
      ></div>
    );
  };

  const BreadCrumb = ({
    category,
    clickCategory,
    subcategory,
    clickSubcategory,
  }: {
    category: Category;
    clickCategory: () => void;
    subcategory?: Subcategory;
    clickSubcategory?: () => void;
  }) => {
    return (
      <>
        {subcategory && (
          <div className={classNames('pl-[24px]', 'text-[14px]')}>
            in{' '}
            <span
              className={classNames(
                'italic',
                hoverTextColorClassesForColor(category.color),
                'cursor-pointer'
              )}
              onClick={clickSubcategory}
            >
              {t(subcategory.label, subcategory.labelDe)}
            </span>
          </div>
        )}
        {category && (
          <div className={classNames('pl-[24px]', 'text-[14px]')}>
            under{' '}
            <span
              className={classNames(
                'italic',
                hoverTextColorClassesForColor(category.color),
                'cursor-pointer'
              )}
              onClick={clickCategory}
            >
              {t(category.label, category.labelDe)}
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={classNames(
        'absolute',
        'inset-x-[10vw]',
        'inset-y-[10vh]',
        'bg-gray-100',
        'rounded-[0.3em]',
        'shadow-md'
      )}
    >
      <div
        className={classNames(
          'h-full',
          'w-full',
          'flex',
          'flex-row',
          'justify-center',
          'p-[24px]'
        )}
      >
        <div
          className={classNames(
            'w-[70%]',
            'relative',
            'text-[14px]',
            'relative'
          )}
        >
          <NewGraphSwitch
            explorationView={explorationView}
            setExplorationView={setExplorationView}
          />
          {explorationView === ExplorationView.Text && (
            <NewGraphText
              enrichedGraph={enrichedGraph}
              selectedIndexes={selectedIndexes}
              setSelectedIndexes={setSelectedIndexes}
            />
          )}
          {explorationView === ExplorationView.Graph && (
            <div className={classNames('h-[85%]', 'relative')}>
              <div
                className={classNames(
                  'absolute',
                  'inset-0',
                  'perspective-[100px]'
                )}
              >
                <div
                  className={classNames(
                    'h-full',
                    'relative',
                    'bg-linear-to-t',
                    'from-gray-200',
                    'to-gray-100',
                    'rotate-x-70',
                    'transform-3d',
                    'origin-bottom'
                  )}
                ></div>
              </div>
              {enrichedGraph.categories.map((category, categoryIndex) => (
                <Fragment key={category.id}>
                  <CategoryButton
                    category={category}
                    categoryIndex={categoryIndex}
                    active={categoryIndex === selectedIndexes.category}
                    childActive={selectedIndexes.subcategory !== -1}
                    color={category.color}
                    onClick={() => {
                      setSelectedIndexes({
                        category: categoryIndex,
                        subcategory: -1,
                        project: -1,
                      });
                    }}
                  />
                </Fragment>
              ))}
              {selectedIndexes.category !== -1 &&
                enrichedGraph.categories[
                  selectedIndexes.category
                ].subcategories.map((subcategory, subcategoryIndex) => (
                  <Fragment key={subcategory.id}>
                    <SubcategoryButton
                      subcategory={subcategory}
                      subcategoryIndex={subcategoryIndex}
                      active={subcategoryIndex === selectedIndexes.subcategory}
                      childActive={selectedIndexes.project !== -1}
                      color={
                        enrichedGraph.categories[selectedIndexes.category].color
                      }
                      onClick={() => {
                        setSelectedIndexes({
                          category: selectedIndexes.category,
                          subcategory: subcategoryIndex,
                          project: -1,
                        });
                      }}
                    />
                    <Line
                      from={coordinateForCategory(selectedIndexes.category)}
                      to={coordinateForSubcategory(subcategoryIndex)}
                      active={subcategoryIndex === selectedIndexes.subcategory}
                    />
                  </Fragment>
                ))}
              {selectedIndexes.subcategory !== -1 &&
                enrichedGraph.categories[
                  selectedIndexes.category
                ].subcategories[selectedIndexes.subcategory].projects.map(
                  (project, projectIndex) => (
                    <Fragment key={project.id}>
                      <ProjectButton
                        project={project}
                        projectIndex={projectIndex}
                        active={projectIndex === selectedIndexes.project}
                        color={
                          enrichedGraph.categories[selectedIndexes.category]
                            .color
                        }
                        onClick={() => {
                          setSelectedIndexes({
                            category: selectedIndexes.category,
                            subcategory: selectedIndexes.subcategory,
                            project: projectIndex,
                          });
                        }}
                      />
                      {selectedIndexes.project !== -1 &&
                        selectedIndexes.project === projectIndex && (
                          <>
                            <Button
                              styling={'round'}
                              active={false}
                              childActive={false}
                              color={
                                enrichedGraph.categories[
                                  selectedIndexes.category
                                ].color
                              }
                              onClick={() => {
                                loadProject(project);
                              }}
                              position={{
                                x: coordinateForProject(projectIndex).x,
                                y: coordinateForProject(projectIndex).y + 100,
                              }}
                            >
                              <div>
                                <ArrowUp width={14} />
                              </div>
                              <div className={classNames('text-[.7em]')}>
                                {t('LOAD', 'LADEN')}
                              </div>
                            </Button>
                            <Line
                              from={coordinateForProject(projectIndex)}
                              to={{
                                x: coordinateForProject(projectIndex).x,
                                y: coordinateForProject(projectIndex).y + 110,
                              }}
                              active={false}
                            />
                          </>
                        )}
                      <Line
                        from={coordinateForSubcategory(
                          selectedIndexes.subcategory
                        )}
                        to={coordinateForProject(projectIndex)}
                        active={projectIndex === selectedIndexes.project}
                      />
                    </Fragment>
                  )
                )}
              {selectedIndexes.category === -1 && (
                <div
                  className={classNames(
                    'absolute',
                    'top-1/2',
                    '-translate-y-1/2',
                    'left-1/2',
                    '-translate-x-1/2',
                    'text-center',
                    'text-[50px]',
                    'leading-[1.15em]',
                    'min-w-[550px]',
                    'p-[40px]',
                    'bg-white/40',
                    'rounded-[12px]'
                  )}
                >
                  Climate and Water
                  <br />
                  under Change
                </div>
              )}
              {/* end without tilt */}
            </div>
          )}
        </div>
        {/* START SIDEBAR */}
        <div
          className={classNames(
            'w-[30%]',
            'h-full',
            'overflow-y-auto',
            'border-l-[1px]',
            'border-gray-200',
            'pl-[24px]'
          )}
          ref={sidebarRef}
        >
          {(() => {
            if (selection === null) {
              return (
                <div
                  className={classNames(
                    'h-full',
                    'flex',
                    'flex-col',
                    'justify-center',
                    'items-center'
                  )}
                >
                  <div
                    className={classNames(
                      'p-[15px]',
                      'text-[18px]',
                      'text-center'
                    )}
                  >
                    {t(
                      `In this overview you will find a plethora of topics, stories and data. Navigate into the topic tree and through project relations and load a project into the explorer.`,
                      `In dieser Übersicht finden Sie zahlreiche Themen, Geschichten und Daten. Navigieren Sie durch den Themenbaum und Projektbeziehungen und laden Sie ein Projekt in den Explorer.`
                    )}
                  </div>
                  <div
                    className={classNames(
                      'p-[15px]',
                      'text-[18px]',
                      'text-center'
                    )}
                  >
                    {t(
                      'You can start with the colored categories at the bottom.',
                      'Sie können mit den farbigen Kategorien weiter unten beginnen.'
                    )}
                  </div>
                  <div
                    className={classNames(
                      'rotate-180',
                      'text-center',
                      'text-[48px]'
                    )}
                  >
                    ☞
                  </div>
                  <div
                    className={classNames(
                      'p-[15px]',
                      'text-[18px]',
                      'text-center'
                    )}
                  >
                    {t(
                      'You can get back here anytime by clicking on this icon in the bottom right of the screen',
                      'Sie können jederzeit hierhin zurückkehren indem Sie auf dieses Symbol klicken'
                    )}
                  </div>
                  <div className={classNames('w-[120px]', 'py-[20px]')}>
                    <GraphLink />
                  </div>
                </div>
              );
            }
            switch (selection.type) {
              case SelectionType.Project: {
                const project = selection.project;
                return (
                  <>
                    <h2
                      className={classNames(
                        'relative',
                        'max-w-[600px]',
                        'mx-auto',
                        'pl-[24px]',
                        'pr-[36px]',
                        'mb-[12px]'
                      )}
                    >
                      {t(selection.project.name, selection.project.nameDe)}
                      <div
                        className={classNames(
                          'absolute',
                          'left-0',
                          'top-0',
                          'bottom-0',
                          'w-[12px]',
                          'rounded-[6px]',
                          classNames(
                            gradientClassesForColor(selection.category.color)
                          )
                        )}
                      ></div>
                    </h2>
                    <div className={classNames('mb-[24px]')}>
                      <BreadCrumb
                        category={selection.category}
                        clickCategory={() =>
                          setSelectedIndexes({
                            category: selectedIndexes.category,
                            subcategory: -1,
                            project: -1,
                          })
                        }
                        subcategory={selection.subcategory}
                        clickSubcategory={() =>
                          setSelectedIndexes({
                            category: selectedIndexes.category,
                            subcategory: selectedIndexes.subcategory,
                            project: -1,
                          })
                        }
                      ></BreadCrumb>
                    </div>

                    <div className={classNames('max-w-[600px]', 'mx-auto')}>
                      {project.contentFormat !== ContentFormat.Html &&
                        project.shortContent
                          .split('\n')
                          .map((line, index) => <p key={index}>{line}</p>)}
                      {project.contentFormat == ContentFormat.Html && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: t(
                              project.shortContent,
                              project.shortContentDe
                            ),
                          }}
                        ></div>
                      )}
                    </div>
                    {Boolean(relatedToSelected.length) && (
                      <div>
                        <h3>
                          {t(
                            'Related topics to be added to the view',
                            'Verwandte Themen werden der Karte hinzugefügt.'
                          )}
                        </h3>
                        {relatedToSelected.map(
                          (relatedProject, relatedIndex) => (
                            <div
                              key={relatedProject.id}
                              onClick={() => {
                                setIndexesForProjectId(relatedProject.id);
                              }}
                              className={classNames(
                                'cursor-pointer',
                                hoverTextColorClassesForColor(
                                  categoryById(
                                    subcategoryById(
                                      relatedProject.subcategoryId
                                    ).categoryId
                                  ).color
                                )
                              )}
                            >
                              <span
                                className={classNames(
                                  'inline-block',
                                  'rounded-full',
                                  backgroundColorClassesForColor(
                                    categoryById(
                                      subcategoryById(
                                        relatedProject.subcategoryId
                                      ).categoryId
                                    ).color
                                  ),
                                  'w-[12px]',
                                  'h-[12px]',
                                  'mr-[6px]',
                                  relatedIndex ? 'mt-[12px]' : undefined
                                )}
                              ></span>
                              {t(relatedProject.name, relatedProject.nameDe)}
                            </div>
                          )
                        )}
                      </div>
                    )}

                    <div className={classNames('mt-[24px]', 'text-center')}>
                      <NeumorphBox
                        className={classNames(
                          'flex',
                          'flex-col',
                          'justify-center'
                        )}
                        type={NeumorphType.Hoverable}
                        onClick={() => {
                          loadProject(project);
                        }}
                      >
                        {t('LOAD PROJECT', 'PROJEKT LADEN')}
                      </NeumorphBox>
                    </div>
                  </>
                );
              }
              case SelectionType.Subcategory: {
                const subcategory = selection.subcategory;
                return (
                  <>
                    <h2
                      className={classNames(
                        'relative',
                        'max-w-[600px]',
                        'mx-auto',
                        'pl-[24px]',
                        'pr-[36px]',
                        'mb-[12px]'
                      )}
                    >
                      {t(
                        selection.subcategory.label,
                        selection.subcategory.labelDe
                      )}
                      <div
                        className={classNames(
                          'absolute',
                          'left-0',
                          'top-0',
                          'bottom-0',
                          'w-[12px]',
                          'rounded-[6px]',
                          classNames(
                            gradientClassesForColor(selection.category.color)
                          )
                        )}
                      ></div>
                    </h2>
                    <div className={classNames('mb-[24px]')}>
                      <BreadCrumb
                        category={selection.category}
                        clickCategory={() =>
                          setSelectedIndexes({
                            category: selectedIndexes.category,
                            subcategory: -1,
                            project: -1,
                          })
                        }
                      ></BreadCrumb>
                    </div>
                    <div className={classNames('max-w-[600px]', 'mx-auto')}>
                      {t(
                        selection.subcategory.description,
                        selection.subcategory.descriptionDe
                      )
                        .split('\n')
                        .map((line, index) => (
                          <p key={index}>{line}</p>
                        ))}
                    </div>
                    <div>
                      <h3>{t('Read more on', 'Mehr über')}</h3>
                      {subcategory.projects.map((project, projectIndex) => (
                        <div
                          key={project.id}
                          onClick={() => {
                            setSelectedIndexes((current) => ({
                              category: current.category,
                              subcategory: current.subcategory,
                              project: projectIndex,
                            }));
                          }}
                          className={classNames(
                            'cursor-pointer',
                            hoverTextColorClassesForColor(
                              selection.category.color
                            )
                          )}
                        >
                          <span
                            className={classNames(
                              'inline-block',
                              'rounded-full',
                              backgroundColorClassesForColor(
                                selection.category.color
                              ),
                              'w-[12px]',
                              'h-[12px]',
                              'mr-[6px]',
                              projectIndex ? 'mt-[12px]' : undefined
                            )}
                          ></span>
                          {t(project.shortName, project.shortNameDe)}
                        </div>
                      ))}
                    </div>
                  </>
                );
              }
              case SelectionType.Category: {
                const category = selection.category;
                return (
                  <>
                    <h2
                      className={classNames(
                        'relative',
                        'max-w-[600px]',
                        'mx-auto',
                        'pl-[24px]',
                        'pr-[36px]'
                      )}
                    >
                      {t(category.label, category.labelDe)}
                      <div
                        className={classNames(
                          'absolute',
                          'left-0',
                          'top-0',
                          'bottom-0',
                          'w-[12px]',
                          'rounded-[6px]',
                          classNames(gradientClassesForColor(category.color))
                        )}
                      ></div>
                    </h2>
                    <div className={classNames('max-w-[600px]', 'mx-auto')}>
                      {t(category.description, category.descriptionDe)
                        .split('\n')
                        .map((line, index) => (
                          <p key={index}>{line}</p>
                        ))}
                    </div>
                    <div>
                      <h3>
                        {t(
                          'Find out about these topics',
                          'Mehr über diese Themen'
                        )}
                      </h3>
                      {selection.category.subcategories.map(
                        (subcategory, subcategoryIndex) => (
                          <div
                            key={subcategory.id}
                            onClick={() => {
                              setSelectedIndexes((current) => ({
                                category: current.category,
                                subcategory: subcategoryIndex,
                                project: -1,
                              }));
                            }}
                            className={classNames(
                              'cursor-pointer',
                              hoverTextColorClassesForColor(
                                selection.category.color
                              )
                            )}
                          >
                            <span
                              className={classNames(
                                'inline-block',
                                'rounded-full',
                                backgroundColorClassesForColor(
                                  selection.category.color
                                ),
                                'w-[12px]',
                                'h-[12px]',
                                'mr-[6px]',
                                subcategoryIndex ? 'mt-[12px]' : undefined
                              )}
                            ></span>
                            {t(subcategory.label, subcategory.labelDe)}
                          </div>
                        )
                      )}
                    </div>
                  </>
                );
              }
            }
          })()}
        </div>
        {/* END SIDEBAR */}
      </div>
    </div>
  );
}
